import React, { useEffect, useRef, useState } from "react";
import { ErrorInputIcon } from "../../../icons/ErrorInputIcon";
import { useMutation } from "@apollo/client";
import UpdateUserName from "../../../graphql/UpdateUserName.graphql";

const useNameInputsWithUpdate = ({
  isDisabled = false,
}: {
  isDisabled?: boolean;
}): {
  isNameInputValid: boolean;
  handleUpdateName: () => Promise<{ firstName: string; lastName: string }>;
  render: JSX.Element;
} => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const firstNameReference = useRef<string>();
  firstNameReference.current = firstName;

  const lastNameReference = useRef<string>();
  lastNameReference.current = lastName;

  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");

  const [updateUserName, { data, loading, error }] = useMutation(UpdateUserName);

  const handleErrors = () => {
    let firstNameError = "";
    let lastNameError = "";

    if (
      (firstName.trim().length !== 0 && firstName.trim().length < 2) ||
      !/^[\p{L}\p{M}'\s]*$/u.test(firstName) ||
      firstName.trim().length > 120
    ) {
      firstNameError = "Please enter a valid first name";
    }
    if (
      (lastName.trim().length !== 0 && lastName.trim().length < 2) ||
      !/^[\p{L}\p{M}'\s]*$/u.test(lastName) ||
      lastName.trim().length > 120
    ) {
      lastNameError = "Please enter a valid last name";
    }

    setFirstNameError(firstNameError);
    setLastNameError(lastNameError);

    if (firstNameError || lastNameError) {
      return firstNameError || lastNameError;
    }
  };

  useEffect(() => {
    handleErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, lastName]);

  const handleUpdateName = async () => {
    try {
      const errors = handleErrors();
      if (errors) {
        throw errors;
      }
      const input = { firstName: firstNameReference.current, lastName: lastNameReference.current };
      const { data } = await updateUserName({ variables: { input } });
      console.log(`User name updated with: ${data.updateUser.firstName} ${data.updateUser.lastName}`);
      return {
        firstName: data.updateUser.firstName,
        lastName: data.updateUser.lastName,
      };
    } catch (error) {
      console.log(error);
      throw Error("Couldn't update user first name & last name.");
    }
  };

  return {
    isNameInputValid:
      firstNameError === "" &&
      lastNameError === "" &&
      firstName.trim().length > 2 &&
      lastName.trim().length > 2,
    handleUpdateName: handleUpdateName,
    render: (
      <>
        {/* First name */}
        <div className="w-full relative mb-4">
          <input
            placeholder=" "
            className={`flex-1 ${
              firstNameError ? "border-red-100 border-opacity-75 border-2 shadow-md" : "border-grey"
            } border appearance-none rounded-lg w-full px-3.5 pt-4 pb-3 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 focus:shadow-md transition-all focus:outline-none focus:border-transparent ${
              isDisabled || loading ? "opacity-75 cursor-not-allowed pointer-events-none" : ""
            }`}
            autoComplete="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={isDisabled || loading ? true : false}
          />
          <label
            htmlFor="firstName"
            className="text-grey absolute top-0 left-0 py-3.5 px-5 text-lg duration-300 origin-0 w-full cursor-text pointer-events-none select-none"
          >
            First name
          </label>
          {firstNameError && (
            <div className="flex items-center mt-1">
              <ErrorInputIcon className="mr-1" />
              <span className="text-red-100 text-xs block">{firstNameError}</span>
            </div>
          )}
        </div>

        {/* Last name */}
        <div className="w-full relative mb-4">
          <input
            placeholder=" "
            className={`flex-1 ${
              lastNameError ? "border-red-100 border-opacity-75 border-2 shadow-md" : "border-grey"
            } border appearance-none rounded-lg w-full px-3.5 pt-4 pb-3 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 focus:shadow-md transition-all focus:outline-none focus:border-transparent ${
              isDisabled || loading ? "opacity-75 cursor-not-allowed pointer-events-none" : ""
            }`}
            autoComplete="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={isDisabled || loading ? true : false}
          />
          <label
            htmlFor="lastName"
            className="text-grey absolute top-0 left-0 py-3.5 px-5 text-lg duration-300 origin-0 w-full cursor-text pointer-events-none select-none"
          >
            Last name
          </label>
          {lastNameError && (
            <div className="flex items-center mt-1">
              <ErrorInputIcon className="mr-1" />
              <span className="text-red-100 text-xs block">{lastNameError}</span>
            </div>
          )}
        </div>
      </>
    ),
  };
};

export default useNameInputsWithUpdate;
